import type {
    ConversationOpenTarget,
    DocumentOpenTarget,
    HostOpenDocumentIntent,
    KeepOfflineTarget,
    OpenTarget,
    ScreenOpenTarget,
} from "./Platform";
import { SCREENS, screenByKey, screenByPath, type ScreenKey } from "../screens/registry";
import {
    appendRevisionParams,
    buildNavigationUrl,
    conversationIdFromPath,
    isDocumentAlias,
    parseDocumentPageParam,
    parseSearchHighlightParam,
} from "../utils/navigation";

/**
 * The typed open targets and the ONE place that maps them to and from the web's
 * routes. Shared code builds a target here and hands it to `Platform.open`; the web
 * adapter renders it with `urlForTarget`; the desktop maps it to a workspace
 * destination by key or id, never by parsing a path. A caller that still holds a
 * web URL (a legacy string route) types it once with `targetForUrl`.
 */

/** The Questions screen's thread route: `QUESTIONS_PATH` (`/c`) + `/:id`. */
const THREAD_PATH_PREFIX = "/c/";
const DOCUMENT_PATH_PATTERN = /^\/doc\/([^/?#]+)(?:\/sg\/([^/?#]+))?/;
/** A base for parsing a relative route. A URL that resolves to another origin is external. */
const RELATIVE_ROUTE_BASE = "http://open-target.invalid";

export function screenTarget(screen: ScreenKey, params?: Record<string, string>): ScreenOpenTarget {
    return params ? { kind: "screen", screen, params } : { kind: "screen", screen };
}

export function conversationTarget(
    conversationId: string,
    params?: Record<string, string>,
): ConversationOpenTarget {
    return params
        ? { kind: "conversation", conversationId, params }
        : { kind: "conversation", conversationId };
}

export function documentTarget(
    documentId: string,
    intent: Omit<HostOpenDocumentIntent, "documentId"> = {},
): DocumentOpenTarget {
    return { kind: "document", documentId, ...intent };
}

/** A row's "Keep offline" toggle target, or `null` when the row doesn't open a cloud
 *  document (a screen target, a local file, ...). Shared by Overview and Portfolio,
 *  whose rows differ only in which extra fields they carry alongside `target`. */
export function keepOfflineTargetFor(target: OpenTarget): KeepOfflineTarget | null {
    if (!("documentId" in target) || !target.documentId) return null;
    return {
        documentId: target.documentId,
        ...(target.fileId === undefined ? {} : { fileId: target.fileId }),
        ...(target.sectionGroupId === undefined ? {} : { sectionGroupId: target.sectionGroupId }),
    };
}

function withParams(path: string, params: Record<string, string> | undefined): string {
    const search = params ? new URLSearchParams(params).toString() : "";
    return search ? `${path}?${search}` : path;
}

/** The thread path a host's Questions router mounts at (`/c/<id>[?q=...]`). */
export function conversationUrl(target: ConversationOpenTarget): string {
    return withParams(`${THREAD_PATH_PREFIX}${target.conversationId}`, target.params);
}

/**
 * Which variant an `OpenTarget` is.
 *
 * Every platform target has a `kind`; the compiler narrows direct switches and rejects a
 * malformed mixture such as `{ screen, documentId }`. `HostOpenDocumentIntent` remains an
 * untagged native transport payload, while `DocumentOpenTarget` intersects its tag here.
 */
export type OpenTargetVariant = OpenTarget["kind"];

export function openTargetVariant(target: OpenTarget): OpenTargetVariant {
    return target.kind;
}

/**
 * The web URL of a target. A document intent becomes its `/doc/...` deep link with the
 * `?hl=`, `?page=` and `?revision=` params the viewer reads. A plain PDF retains its
 * page with `?page=`; a section group already names a sheet. A screen resolves through the registry, so
 * an unregistered key has no URL. Local-file and favorite targets are desktop-only and
 * have no URL either.
 */
export function urlForTarget(target: OpenTarget): string | null {
    switch (target.kind) {
        case "url":
            return target.url;
        case "screen": {
            const path = screenByKey(SCREENS, target.screen)?.path;
            return path === undefined ? null : withParams(path, target.params);
        }
        case "conversation":
            return conversationUrl(target);
        case "document":
            break;
        default:
            // Local-file and favorite targets are desktop-only; they have no web URL.
            return null;
    }
    const intent = target;
    if (!intent.documentId) return null;
    const base = buildNavigationUrl({
        doc: intent.documentId,
        sectionGroup: intent.sectionGroupId,
        searchHighlight: intent.highlight,
        // A section group is already a sheet location; only a plain PDF needs a page query.
        page: !intent.sectionGroupId && !intent.highlight ? intent.page : undefined,
    });
    return appendRevisionParams(base, intent.revision, undefined);
}

function documentIntentForRoute(
    pathname: string,
    searchParams: URLSearchParams,
): DocumentOpenTarget | null {
    const match = DOCUMENT_PATH_PATTERN.exec(pathname);
    if (!match || isDocumentAlias(match[1])) return null;
    const [, documentId, sectionGroupId] = match;
    const highlight = parseSearchHighlightParam(searchParams) ?? undefined;
    const page = parseDocumentPageParam(searchParams) ?? undefined;
    const revision = searchParams.get("revision") ?? undefined;
    return {
        kind: "document",
        documentId,
        ...(sectionGroupId ? { sectionGroupId } : {}),
        ...(highlight ? { page: highlight.page, highlight } : page !== undefined ? { page } : {}),
        ...(revision ? { revision } : {}),
    };
}

/**
 * The typed target a web route names, for a caller that still holds a URL string. A
 * `/doc/...` deep link, a `/c/<id>` thread and a registered screen's path type; the
 * route's query becomes the target's params (a document's `hl`/`revision` become its
 * intent fields). Anything else -- an external link, a route no registry screen
 * covers -- stays `{ url }`. The inverse of `urlForTarget` for every typed target.
 */
export function targetForUrl(url: string): OpenTarget {
    let route: URL;
    try {
        route = new URL(url, RELATIVE_ROUTE_BASE);
    } catch {
        return { kind: "url", url };
    }
    if (route.origin !== RELATIVE_ROUTE_BASE) return { kind: "url", url };
    const document = documentIntentForRoute(route.pathname, route.searchParams);
    if (document) return document;
    const params = route.search ? Object.fromEntries(route.searchParams) : undefined;
    const conversationId = conversationIdFromPath(route.pathname);
    if (conversationId !== null) return conversationTarget(conversationId, params);
    const screen = screenByPath(SCREENS, route.pathname);
    if (screen) return screenTarget(screen.key, params);
    return { kind: "url", url };
}
